import { proxy } from 'comlink';
import { Observable } from 'rxjs';
import { fromFetch } from 'rxjs/fetch';
import { switchMap } from 'rxjs/operators';
import { HttpRequest, HttpRequestInit } from '../http-request/http-request';
import { HttpResponse } from '../http-response/http-response';
import { RetryOptions } from '../../model/retry-options/retry-options';
import { HttpPollingRequestParam } from '../../model/directory/http-polling-request';

/** Http Interface class for making api calls to backend */
export class HttpClient {
  /**
   *
   * @param url - url for the api
   * @param request - request object associated with the api
   * @returns returns http response from the get api
   *
   * ```
   * @example
   * get('https://nice.dev.cxone.com/getUsers', {body: 'api_request_body'})
   * ```
   */
  static get(url: string, request: HttpRequestInit): Promise<HttpResponse> {
    const httpRequest = new HttpRequest('GET', request);
    return this.handleResponse(fromFetch(url, httpRequest.toRequestInit())).toPromise();
  }

  /**
   * Calls given endpoint on worker thread
   * @param request - Http request with headers and url
   * @param callback - function to execute after polling
   * @param startPolling - function to perform polling on worker thread
   * @param options - optional parameters - retryOptions - retry interval and maxRetryAttempts, isPolling - Single api call or polling
   * ```
   * @example
   * getWithPolling({url:'https://nice.dev.cxone.com/getUsers', request:{headers: 'api_request_header'}}, this.onPollingExecuted, startPolling, retryOptions)
   * ```
   */
  static async getWithPolling(
    request: Array<HttpPollingRequestParam> | HttpPollingRequestParam,
    callback: (data: any) => void,
    startPolling: any,
    options: { retryOptions?: RetryOptions; isPolling?: boolean } = {}
  ) {
    await startPolling(request, options.retryOptions, proxy(callback.bind(this)), options.isPolling);
  }

  /**
   *
   * @param url - url for the post api
   * @param request - request object associated with the api
   * @returns returns http response from the post api
   *
   * ```
   * @example
   * get('https://nice.dev.cxone.com/getUsers', {body: 'api_request_body'})
   * ```
   */
  static post(url: string, request: HttpRequestInit): Promise<HttpResponse> {
    const httpRequest = new HttpRequest('POST', request);
    return this.handleResponse(fromFetch(url, httpRequest.toRequestInit())).toPromise();
  }
  /**
   *
   * @param url - url for the put api
   * @param request - request object associated with the api
   * @returns returns http response from the put api
   *
   * ```
   * @example
   * put('https://nice.dev.cxone.com/getUsers', {body: 'api_request_body'})
   * ```
   */
  static put(url: string, request: HttpRequestInit): Promise<HttpResponse> {
    const httpRequest = new HttpRequest('PUT', request);
    return this.handleResponse(fromFetch(url, httpRequest.toRequestInit())).toPromise();
  }

  /**
   *
   * @param url - url for the patch api
   * @param request - request object associated with the api
   * @returns returns http response from the patch api to update data
   *
   * ```
   * @example
   * patch('https://nice.dev.cxone.com/getUsers', {body: 'api_request_body'})
   * ```
   */
  static patch(url: string, request: HttpRequestInit): Promise<HttpResponse> {
    const httpRequest = new HttpRequest('PATCH', request);
    return this.handleResponse(fromFetch(url, httpRequest.toRequestInit())).toPromise();
  }

  /**
   *
   * @param url - url for the delete api
   * @param request - request object associated with the api
   * @returns returns http response from the delete api
   *
   * ```
   * @example
   * delete('https://nice.dev.cxone.com/getUsers', {body: 'api_request_body'})
   * ```
   */
  static delete(url: string, request: HttpRequestInit): Promise<HttpResponse> {
    const httpRequest = new HttpRequest('DELETE', request);
    return this.handleResponse(fromFetch(url, httpRequest.toRequestInit())).toPromise();
  }

  /**
   *
   * @param response$ - Http response object
   * @param retryOptions - retry mechanism for failure scenarios
   * @returns - handled response after api processing
   * ```
   * @example
   * const resp = this.handleResponse({status: 200, statusText: 'OK'}, {maxRetryAttempts: 3, retryInterval: 1000});
   * ```
   */
  static handleResponse(response$: Observable<Response>, retryOptions?: RetryOptions): Observable<any> {
    retryOptions = retryOptions || RetryOptions.default();
    return response$.pipe(
      switchMap((response: any) => {
        return new Promise((resolve, reject) => {
          response.text().then((text: string) => {
            if (response.status >= 400 && retryOptions && retryOptions.maxRetryAttempts > 0) {
              const retryAfter = Number(response.headers['Retry-After']);
              const retryInterval = retryAfter ? retryAfter * 1000 : retryOptions.retryInterval;
              setTimeout(() => {
                return this.handleResponse(response$, retryOptions);
              }, retryInterval);
              retryOptions.maxRetryAttempts--;
            }
            let data: any;
            try {
              data = JSON.parse(text);
            } catch (err) {
              console.error('Parse Error: ', err);
              data = text;
            }
            const httpResp = new HttpResponse(response, data);
            if (response.ok) {
              resolve(httpResp);
            } else {
              reject(httpResp);
            }
          });
        });
      })
    );
  }
}
